// modals/bank/TransferModal.jsx
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Save,
    X,
    DollarSign,
    Calendar,
    Hash,
    FileText,
    Repeat
} from 'react-feather';
import Select from 'react-select';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import { bankActions } from '../../action/bankActions';

const TransferModal = ({ accounts = [], onSuccess, onClose }) => {
    const [formData, setFormData] = useState({
        from_account_id: '',
        to_account_id: '',
        transfer_date: dayjs().format('YYYY-MM-DD'),
        amount: '',
        reference_no: '',
        description: '',
        status: 'completed'
    });

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [fromAccount, setFromAccount] = useState(null);
    const [accountOptions, setAccountOptions] = useState([]);

    // Calculate new balances for preview
    const [fromNewBalance, setFromNewBalance] = useState(null);
    const [toNewBalance, setToNewBalance] = useState(null);

    // Status options
    const statusOptions = [
        { value: 'completed', label: 'Completed' },
        { value: 'pending', label: 'Pending' },
        { value: 'cancelled', label: 'Cancelled' }
    ];

    useEffect(() => {
        if (accounts.length > 0) {
            const options = accounts
                .filter(acc => acc.status === 'active')
                .map(acc => ({
                    value: acc.id,
                    label: `${acc.account_name} (${acc.bank_name}) - ${bankActions.formatCurrency(acc.current_balance)}`,
                    balance: acc.current_balance,
                    currency: acc.currency,
                    account: acc
                }));
            setAccountOptions(options);
        }
    }, [accounts]);

    // Update balance preview when amount or accounts change
    useEffect(() => {
        const amount = parseFloat(formData.amount) || 0;

        if (fromAccount) {
            setFromNewBalance(fromAccount.current_balance - amount);
        } else {
            setFromNewBalance(null);
        }

        if (formData.to_account_id) {
            const toAcc = accounts.find(acc => acc.id === parseInt(formData.to_account_id));
            if (toAcc) {
                setToNewBalance(toAcc.current_balance + amount);
            }
        } else {
            setToNewBalance(null);
        }
    }, [formData.amount, formData.to_account_id, fromAccount, accounts]);

    // Clean up modal backdrops
    const cleanupModalBackdrops = () => {
        const backdrops = document.querySelectorAll('.modal-backdrop');
        backdrops.forEach(backdrop => backdrop.remove());
        document.body.classList.remove('modal-open');
        document.body.style.paddingRight = '';
        document.body.style.overflow = '';
    };

    // Show Bootstrap modal
    const showBootstrapModal = () => {
        cleanupModalBackdrops();
        const modalElement = document.getElementById('transfer-modal');
        if (modalElement) {
            if (typeof window.$ !== 'undefined' && window.$.fn.modal) {
                window.$(modalElement).modal('show');
            } else if (typeof window.bootstrap !== 'undefined' && window.bootstrap.Modal) {
                let modalInstance = window.bootstrap.Modal.getInstance(modalElement);
                if (!modalInstance) {
                    modalInstance = new window.bootstrap.Modal(modalElement);
                }
                modalInstance.show();
            } else {
                modalElement.classList.add('show');
                modalElement.style.display = 'block';
                const backdrop = document.createElement('div');
                backdrop.className = 'modal-backdrop fade show';
                document.body.appendChild(backdrop);
                document.body.classList.add('modal-open');
            }
        }
    };

    // Hide Bootstrap modal
    const hideBootstrapModal = () => {
        const modalElement = document.getElementById('transfer-modal');
        if (modalElement) {
            if (typeof window.$ !== 'undefined' && window.$.fn.modal) {
                window.$(modalElement).modal('hide');
                setTimeout(cleanupModalBackdrops, 300);
            } else if (typeof window.bootstrap !== 'undefined' && window.bootstrap.Modal) {
                const modalInstance = window.bootstrap.Modal.getInstance(modalElement);
                if (modalInstance) {
                    modalInstance.hide();
                    setTimeout(cleanupModalBackdrops, 300);
                }
            } else {
                modalElement.classList.remove('show');
                modalElement.style.display = 'none';
                cleanupModalBackdrops();
            }
        }
    };

    useEffect(() => {
        setTimeout(() => showBootstrapModal(), 50);
        return () => {
            cleanupModalBackdrops();
        };
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleSelectChange = (name, option) => {
        setFormData(prev => ({ ...prev, [name]: option?.value || '' }));

        if (name === 'from_account_id') {
            setFromAccount(option?.account || null);
        }

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleDateChange = (date) => {
        if (date) {
            setFormData(prev => ({ ...prev, transfer_date: date.format('YYYY-MM-DD') }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.from_account_id) {
            newErrors.from_account_id = 'Please select source account';
        }

        if (!formData.to_account_id) {
            newErrors.to_account_id = 'Please select destination account';
        }

        if (formData.from_account_id && formData.to_account_id &&
            formData.from_account_id === formData.to_account_id) {
            newErrors.to_account_id = 'Source and destination accounts must be different';
        }

        if (!formData.amount || parseFloat(formData.amount) <= 0) {
            newErrors.amount = 'Please enter a valid amount';
        } else if (fromAccount && parseFloat(formData.amount) > fromAccount.current_balance) {
            newErrors.amount = `Insufficient balance. Available: ${bankActions.formatCurrency(fromAccount.current_balance)}`;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            Swal.fire('Error', 'Please fix the errors in the form', 'error');
            return;
        }

        try {
            setLoading(true);

            const dataToSend = {
                ...formData,
                amount: parseFloat(formData.amount)
            };

            const result = await bankActions.transfer(dataToSend);

            if (result.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Transfer completed successfully',
                    timer: 2000,
                    showConfirmButton: false
                });

                hideBootstrapModal();
                if (onSuccess) onSuccess();
            } else {
                Swal.fire('Error', result.message || 'Failed to transfer', 'error');
            }
        } catch (error) {
            console.error('Error transferring:', error);
            Swal.fire('Error', 'Failed to transfer', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        if (formData.amount || formData.description) {
            Swal.fire({
                title: 'Discard Changes?',
                text: 'You have unsaved changes. Are you sure?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, discard'
            }).then((result) => {
                if (result.isConfirmed) {
                    hideBootstrapModal();
                    if (onClose) onClose();
                }
            });
        } else {
            hideBootstrapModal();
            if (onClose) onClose();
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    // Get destination account details for preview
    const getDestinationAccount = () => {
        if (!formData.to_account_id) return null;
        return accounts.find(acc => acc.id === parseInt(formData.to_account_id));
    };

    const destinationAccount = getDestinationAccount();

    return (
        <div className="modal fade" id="transfer-modal" tabIndex="-1" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            <Repeat size={18} className="me-2" />
                            Transfer Between Accounts
                        </h5>
                        <button type="button" className="btn-close" onClick={handleCancel}></button>
                    </div>

                    <div className="modal-body">
                        <form onSubmit={handleSubmit}>
                            {/* Balance Preview Cards */}
                            {(fromAccount || destinationAccount) && (
                                <div className="card bg-light mb-3">
                                    <div className="card-body py-2">
                                        <div className="row">
                                            {fromAccount && (
                                                <div className="col-6">
                                                    <small className="text-muted">From: {fromAccount.account_name}</small>
                                                    <div className="d-flex justify-content-between">
                                                        <span>Current:</span>
                                                        <span>{formatCurrency(fromAccount.current_balance)}</span>
                                                    </div>
                                                    <div className="d-flex justify-content-between text-primary">
                                                        <span>After:</span>
                                                        <span className="fw-bold">{formatCurrency(fromNewBalance)}</span>
                                                    </div>
                                                </div>
                                            )}
                                            {destinationAccount && (
                                                <div className="col-6">
                                                    <small className="text-muted">To: {destinationAccount.account_name}</small>
                                                    <div className="d-flex justify-content-between">
                                                        <span>Current:</span>
                                                        <span>{formatCurrency(destinationAccount.current_balance)}</span>
                                                    </div>
                                                    <div className="d-flex justify-content-between text-success">
                                                        <span>After:</span>
                                                        <span className="fw-bold">{formatCurrency(toNewBalance)}</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* From Account */}
                            <div className="row g-3 mb-3">
                                <div className="col-12">
                                    <label className="form-label fw-bold">
                                        From Account <span className="text-danger">*</span>
                                    </label>
                                    <Select
                                        options={accountOptions}
                                        value={accountOptions.find(opt => opt.value === formData.from_account_id)}
                                        onChange={(opt) => handleSelectChange('from_account_id', opt)}
                                        placeholder="Select source account"
                                        isClearable
                                    />
                                    {errors.from_account_id && (
                                        <div className="text-danger small mt-1">{errors.from_account_id}</div>
                                    )}
                                    {fromAccount && (
                                        <small className="text-muted">
                                            Available Balance: {formatCurrency(fromAccount.current_balance)}
                                        </small>
                                    )}
                                </div>
                            </div>

                            {/* To Account */}
                            <div className="row g-3 mb-3">
                                <div className="col-12">
                                    <label className="form-label fw-bold">
                                        To Account <span className="text-danger">*</span>
                                    </label>
                                    <Select
                                        options={accountOptions.filter(opt => opt.value !== formData.from_account_id)}
                                        value={accountOptions.find(opt => opt.value === formData.to_account_id)}
                                        onChange={(opt) => handleSelectChange('to_account_id', opt)}
                                        placeholder="Select destination account"
                                        isClearable
                                    />
                                    {errors.to_account_id && (
                                        <div className="text-danger small mt-1">{errors.to_account_id}</div>
                                    )}
                                </div>
                            </div>

                            {/* Amount and Date */}
                            <div className="row g-3 mb-3">
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">
                                        <DollarSign size={14} className="me-1" />
                                        Amount <span className="text-danger">*</span>
                                    </label>
                                    <div className="input-group">
                                        <span className="input-group-text">₹</span>
                                        <input
                                            type="number"
                                            className={`form-control ${errors.amount ? 'is-invalid' : ''}`}
                                            name="amount"
                                            value={formData.amount}
                                            onChange={handleChange}
                                            min="0.01"

                                            placeholder="0.00"
                                            autoFocus
                                        />
                                    </div>
                                    {errors.amount && (
                                        <div className="text-danger small mt-1">{errors.amount}</div>
                                    )}
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label fw-bold">
                                        <Calendar size={14} className="me-1" />
                                        Transfer Date
                                    </label>
                                    <DatePicker
                                        value={formData.transfer_date ? dayjs(formData.transfer_date) : null}
                                        onChange={handleDateChange}
                                        format="DD/MM/YYYY"
                                        className="form-control"
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>

                            {/* Reference and Status */}
                            <div className="row g-3 mb-3">
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">
                                        <Hash size={14} className="me-1" />
                                        Reference No
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="reference_no"
                                        value={formData.reference_no}
                                        onChange={handleChange}
                                        placeholder="Transaction reference"
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label fw-bold">Status</label>
                                    <Select
                                        options={statusOptions}
                                        value={statusOptions.find(opt => opt.value === formData.status)}
                                        onChange={(opt) => handleSelectChange('status', opt)}
                                        placeholder="Select status"
                                    />
                                </div>
                            </div>

                            {/* Description */}
                            <div className="row g-3 mb-3">
                                <div className="col-12">
                                    <label className="form-label fw-bold">
                                        <FileText size={14} className="me-1" />
                                        Description / Notes
                                    </label>
                                    <textarea
                                        className="form-control"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        rows="2"
                                        placeholder="Enter description or notes"
                                    />
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="text-end mt-4">
                                <button
                                    type="button"
                                    className="btn btn-secondary me-2"
                                    onClick={handleCancel}
                                >
                                    <X size={14} className="me-1" />
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" />
                                            Transferring...
                                        </>
                                    ) : (
                                        <>
                                            <Save size={14} className="me-1" />
                                            Transfer
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

TransferModal.propTypes = {
    accounts: PropTypes.array.isRequired,
    onSuccess: PropTypes.func,
    onClose: PropTypes.func.isRequired
};

export default TransferModal;